import { instituteToday } from '@/features/teacher/utils';
import type { Weekday } from '@/types/timetable';
import type { SectionWeekEntry } from '@/types/timetableAdmin';

/**
 * The calendar arithmetic the timetable screen runs on.
 *
 * Kept out of the page because every one of these was being done inline, and
 * two of them were done twice with different answers — the "is this today's
 * column" test indexed the working-day list by the day number, so an institute
 * closed on Wednesdays highlighted the wrong column for the rest of the week.
 *
 * Everything here speaks `YYYY-MM-DD` in the institute's own local time. There
 * is deliberately no `toISOString()` anywhere: that converts to UTC first, so
 * for anywhere east of Greenwich a Monday morning comes back as the Sunday.
 */

/** Monday first — how an Indian institute prints its week. */
export const WEEKDAY_ORDER: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

/** `YYYY-MM-DD` for a local date, with no timezone conversion on the way. */
export function toDayString(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Which weekday a date falls on, as the API names them. */
export function weekdayOf(date: Date): Weekday {
  return WEEKDAY_ORDER[(date.getDay() + 6) % 7];
}

/**
 * Midnight today, so week maths never carries a time of day into a comparison.
 *
 * Today is the school's, not the device's — a phone in another zone would
 * otherwise highlight the wrong column. Built as local midnight of that date,
 * since every other function here reads dates in local time.
 */
export function startOfToday(): Date {
  const [year, month, day] = instituteToday().split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** The Monday of the week a date falls in. */
export function mondayOf(date: Date): Date {
  const offset = (date.getDay() + 6) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export interface WeekInfo {
  monday: Date;
  sunday: Date;
  /** `YYYY-MM-DD` for Monday through Sunday, in order. */
  dates: string[];
  /** "17 Aug – 23 Aug 2026". */
  rangeLabel: string;
  /** "This week", "Last week", "In 3 weeks". */
  descriptor: string;
  isCurrent: boolean;
}

/**
 * The week `offset` weeks from the one we are in.
 *
 * Seven days rather than six: which of them the grid draws is the institute's
 * decision, read from `workingDays`, and a screen that only knew about Monday
 * to Saturday could not draw a Sunday-working institute at all.
 */
export function weekInfoFor(offset: number): WeekInfo {
  const monday = addDays(mondayOf(startOfToday()), offset * 7);
  const sunday = addDays(monday, 6);
  const dates = Array.from({ length: 7 }, (_, index) => toDayString(addDays(monday, index)));

  const dayMonth = (date: Date) =>
    date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  return {
    monday,
    sunday,
    dates,
    rangeLabel: `${dayMonth(monday)} – ${dayMonth(sunday)} ${sunday.getFullYear()}`,
    descriptor: describeOffset(offset),
    isCurrent: offset === 0,
  };
}

function describeOffset(offset: number): string {
  if (offset === 0) return 'This week';
  if (offset === -1) return 'Last week';
  if (offset === 1) return 'Next week';
  if (offset < 0) return `${Math.abs(offset)} weeks ago`;
  return `In ${offset} weeks`;
}

/** How many weeks a date's week is from the current one — what the picker sets. */
export function weekOffsetOf(date: Date): number {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const from = mondayOf(startOfToday()).getTime();
  const to = mondayOf(date).getTime();
  return Math.round((to - from) / (7 * DAY_MS));
}

/** How long a period runs, in minutes. Zero when either end is missing. */
/**
 * The number a period form sends. A break sends none: it is placed by its start
 * time, so its number is only a key — the server assigns one to a new break and
 * keeps the one an existing break has — and the form does not ask for it.
 */
export function periodNumberToSend(typed: string, isBreak: boolean): number | undefined {
  return isBreak ? undefined : Number(typed.trim());
}

export function durationMinutes(startTime: string, endTime: string): number {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start === null || end === null) return 0;
  return Math.max(0, end - start);
}

function toMinutes(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** When an institute paper sits — the part of an exam the timetable cares about. */
export interface ExamHours {
  startTime: string | null;
  endTime: string | null;
}

/**
 * Whether a paper keeps a period free of regular lessons: it does when the two
 * overlap. A paper whose timings were never recorded holds the whole day,
 * because nothing says which periods it leaves free.
 */
export function examHoldsPeriod(
  exam: ExamHours,
  period: { startTime: string; endTime: string },
): boolean {
  const examStart = exam.startTime === null ? null : toMinutes(exam.startTime);
  const examEnd = exam.endTime === null ? null : toMinutes(exam.endTime);
  const periodStart = toMinutes(period.startTime);
  const periodEnd = toMinutes(period.endTime);
  if (examStart === null || examEnd === null || periodStart === null || periodEnd === null) {
    return true;
  }
  return examStart < periodEnd && examEnd > periodStart;
}

/** "45 min", "1 hr", "1 hr 30 min" — the summary cards' wording. */
export function describeMinutes(minutes: number): string {
  if (minutes <= 0) return '—';
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/** "09:30 AM", from the 24-hour string the API stores. */
export function formatClockTime(time: string | null): string | null {
  const minutes = time === null ? null : toMinutes(time);
  if (time === null || minutes === null) return null;

  const hour24 = Math.floor(minutes / 60);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${String(hour12).padStart(2, '0')}:${time.slice(3)} ${hour24 < 12 ? 'AM' : 'PM'}`;
}

/** Shown wherever a lesson would put a subject on a day that already has it. */
export const REPEATED_SUBJECT_WARNING = 'This subject is already assigned to this class on this day.';

/**
 * Whether the class already has this subject on this day, not counting the
 * lesson being edited or moved.
 *
 * A warning, never a rule: a double period of Maths or a second language
 * lesson is a real timetable, so the server accepts it and the admin decides.
 */
export function repeatsSubjectOnDay(
  entries: SectionWeekEntry[],
  subjectUuid: string,
  day: Weekday,
  ignoreEntryUuid?: string,
): boolean {
  return entries.some(
    (entry) =>
      entry.uuid !== ignoreEntryUuid && entry.dayOfWeek === day && entry.subject.uuid === subjectUuid,
  );
}
